import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import { DeepPartial } from '../Utils';

import amountTheme from './index.amount';
import IThemeInterface from './common';

const theme: IThemeInterface = merge<IThemeInterface, DeepPartial<IThemeInterface>>(
  cloneDeep(amountTheme),
  {
    name: 'amountDark',

    global: {
      colors: {
        background: '#01212e',
        blackberry: '#022a3a',
        blackberry05: '#0e3747',
        gray800: '#f8f8f8',
        gray700: '#f2f4f5',
        gray600: '#f2f2f2',
        gray500: '#ccd4d8',
        gray400: '#919ea3',
        gray350: '#677f89',
        gray300: '#4a6672',
        gray200: '#264856',
        gray100: '#0E3747',
        gray50: '#063142',

        accent: 'pomegranate',
        accentHover: 'pomegranate120',
        accentDisable: '#71030b',
        black: '#fff',
        error: '#ec6069',
        focus: '#2178c0',
        hover: 'secondary',
        link: '#fff',
        primary: '#fff',
        secondary: '#264856',
        successActive: '#63C698',
        success120: '#194A48',
        warning: 'mango',
        white: 'blackberry',
      },

      outline: { color: 'focus' },
    },

    banner: {
      info: { backgroundColor: 'gray100' },
      warning: {
        inlineLink: {
          color: 'white',
          hoverColor: 'white',
        },
        closeIcon: {
          color: 'white'
        },
      },
      success: {
        inlineLink: {
          color: 'white',
          hoverColor: 'white',
        },
        closeIcon: {
          color: 'white'
        },
      },
    },

    button: {
      base: {
        background: 'accent',
        border: { color: 'accent' },
        font: { color: 'primary' },
      },

      overrides: {
        secondary: {
          state: {
            disabled: {
              background: 'white',
              border: { color: 'gray300' },
              font: { color: 'gray300' },
            },
            hover: {
              background: 'none',
              font: { color: 'electricAvocado' },
              border: { color: 'electricAvocado' },
            },
          },
        },

        link: {
          state: {
            hover: { font: { color: 'electricAvocado' } },
            focus: { outline: { color: 'focus' } },
          },
        },

        text: {
          state: {
            disabled: {
              font: { color: 'gray300' },
            },
            hover: {
              font: { color: 'electricAvocado' }
            },
            focus: { outline: { color: 'focus' } }
          },
        },

        action: {
          state: {
            active: {
              background: 'successActive',
              border: { color: 'electricAvocado' },
              font: { color: 'blackberry' },
            },
            disabled: {
              background: 'white',
              border: { color: 'gray300' },
              font: { color: 'gray300' },
            },
            hover: {
              font: { color: 'electricAvocado' },
              border: { color: 'electricAvocado' },
            }
          }
        }
      },
    },

    datePicker: {
      navButton: {
        color: 'white'
      },
      weekdayLabel: {
        color: 'inherit'
      },
      overlay: {
        backgroundColor: 'gray50'
      },
      dayCell: {
        selected: {
          color: 'blackberry',
          backgroundColor: 'electricAvocado',
        },
        hover: {
          backgroundColor: 'electricAvocado'
        }
      },
      focusRing: {
        outline: { color: 'focus' }
      }
    },

    form: {
      checkbox: {
        icon: { color: 'white' },

        state: {
          checked: {
            backgroundColor: 'electricAvocado',
            border: { color: 'electricAvocado' },
          },

          disabled: {
            backgroundColor: 'white',
            border: { color: 'gray300' },
            label: { font: { color: 'gray300' } },
          },

          disabledChecked: {
            backgroundColor: 'white',
            icon: { color: 'gray300' },
          },

          error: {
            border: { size: 'double', color: 'error' },
            label: { font: { color: 'error' } },
          },

          hover: {
            border: { color: 'electricAvocado' },
          },
        },
      },

      layout: {
        wrapper: {
          row: { margin: { bottom: '3x' } },
          stackRow: {
            margin: {
              base: { right: '0' },
              small: { right: '3x' },
            },
          },
        },
      },

      helperText: {
        font: {
          color: {
            default: 'gray400',
            hover: 'primary',
            disabled: 'gray300',
          },
        },
      },

      input: {
        border: { color: 'gray400' },
        font: { color: 'gray400' },
        iconColor: 'gray400',
        placeholder: 'gray400',

        state: {
          hover: {
            font: { color: 'primary' }
          },

          focus: {
            font: { color: 'primary' }
          },

          disabled: {
            border: { color: 'gray300' },
            font: { color: 'gray300' },
          },
        },
      },

      label: {
        font: {
          color: {
            default: 'gray400',
            hover: 'primary',
            disabled: 'gray300',
          },
        },
      },

      legend: { font: { color: 'gray400' } },

      radio: {
        badge: {
          border: { color: 'gray700' },
          font: { color: 'gray700' },
          backgroundColor: 'transparent',

          state: {
            checked: {
              border: { color: 'electricAvocado', size: 'double' },
              backgroundColor: 'successActive',
              font: { color: 'white' },
            },

            checkedHover: {
              border: { color: 'electricAvocado', size: 'double' },
            },

            disabled: {
              backgroundColor: 'gray100',
              border: { color: 'gray300' },
              font: { color: 'gray500' },
            },

            disabledChecked: {
              backgroundColor: 'gray200',
              border: { color: 'gray200' },
              font: { color: 'gray500' },
            },

            error: {
              font: { color: 'error' },
            },

            focus: { outline: { color: 'focus' } },

            hover: {
              border: { color: 'electricAvocado' },
              font: { color: 'electricAvocado' },
            },
          }
        },

        button: {
          backgroundColor: 'transparent',
          border: { color: 'gray700' },
          checkedBgColor: 'electricAvocado',

          state: {
            checked: {
              border: { color: 'electricAvocado' },
              shadow: { color: 'success120' },
            },

            disabled: {
              backgroundColor: 'gray100',
              border: { color: 'gray300' },
              label: { font: { color: 'gray400' } },
            },

            disabledChecked: {
              backgroundColor: 'gray300',
              shadow: { color: 'gray100', inset: true, shadow: '0 0 0 4px' }
            },

            error: {
              label: { font: { color: 'error' } },
            },

            hover: {
              border: { color: 'electricAvocado' },
            },
          }
        },

        card: {
          backgroundColor: 'white',
          border: { color: 'gray100', size: 'double' },

          state: {
            checked: {
              backgroundColor: 'success120',
              border: { color: 'focus', size: 'double' },
            },

            hover: {
              border: { color: 'black', side: 'bottom', size: 'double' },
            },

            focus: {
              border: { color: 'transparent', size: 'double' },
              outline: { offset: '5px', style: 'auto' },
            },
          },
        },

        toggle: {
          state: {
            checked: {
              button: {
                border: { color: 'success' },
                backgroundColor: 'successActive',
              },
              slidingIndicator: { color: 'white' },
            },

            disabled: {
              label: { color: 'gray400' },
              button: {
                border: { color: 'gray300' },
                backgroundColor: 'gray100',
              },
              slidingIndicator: { color: 'gray300' },
            },

            disabledChecked: {
              label: { color: 'gray400' },
              button: {
                border: { color: 'gray300' },
                backgroundColor: 'gray300',
              },
            },

            hover: {
              button: { border: { color: 'success' }, backgroundColor: 'transparent' },
              slidingIndicator: { color: 'success' },
            },
          },
        },
      },

      textarea: {
        border: { color: 'gray400' },
        state: {
          hover: {
            border: { color: 'black' },
            font: { color: 'black' },
          },
          disabled: {
            border: { color: 'gray300' },
            font: { color: 'gray300' },
          },
          error: {
            border: { color: 'error' },
          },
        },
      }
    },

    layout: {
      header: {
        backgroundColor: 'gray50',
        shadow: '3x',
      },
    },

    modal: {
      card: {
        backgroundColor: 'gray50',
        border: { color: 'gray300', size: 'double' },
      },
    },

    notification: {
      variant: {
        success: {
          backgroundColor: 'gray50',
          border: {
            side: 'left',
            size: '6px',
            color: 'electricAvocado',
            style: 'solid'
          },
          shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
          padding: { vertical: '2x', horizontal: '3x' }
        },
        error: {
          backgroundColor: 'gray50',
          border: {
            side: 'left',
            size: '6px',
            color: 'error',
            style: 'solid'
          },
          shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
          padding: { vertical: '2x', horizontal: '3x' }
        },
        warning: {
          backgroundColor: 'gray50',
          border: {
            side: 'left',
            size: '6px',
            color: 'warning',
            style: 'solid'
          },
          shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
          padding: { vertical: '2x', horizontal: '3x' }
        }
      }
    },

    spinner: {
      color: {
        circleOutline: 'gray200',
        animatedOutline: 'electricAvocado',
      },
    },

    table: {
      tableHeader: {
        backgroundColor: 'transparent',
        rowDivider: 'gray300',
      },

      tableBody: {
        rowDivider: 'gray300',
      },
    },

    tooltip: {
      pane: {
        background: 'gray200',
      },

      icon: {
        color: 'primary',
        hover: 'electricAvocado',
      },
    },

    typography: {
      pill: {
        neutral: {
          backgroundColor: 'gray350',
          font: { color: 'black' },
        },
        success: {
          backgroundColor: 'success',
          font: { color: 'white' },
        },
        warning: {
          backgroundColor: 'warning',
          font: { color: 'white' },
        },
        error: {
          backgroundColor: 'error',
          font: { color: 'white' },
        },
        info: {
          font: { color: 'white' },
        }
      },
    }
  }
);

export default theme;
